// ---------------------------------------------------------------------------
// Icon Map — centralized icon registry (Page Editor ICON_MAP parity).
//
// Single source of truth: named imports only, no wildcard `import * as Icons`.
// Matches Page Editor element-palette.tsx / layer-tree.tsx pattern.
// ---------------------------------------------------------------------------

import type { LucideIcon } from 'lucide-react'
import {
  // Node types & flow control
  Zap,
  Play,
  PhoneIncoming,
  GitBranch,
  Repeat,
  Layers,
  Group,
  StickyNote,
  GitFork,

  // API & Data
  Globe,
  Database,
  Search,
  Sparkles,

  // Navigation
  Compass,
  ArrowLeft,
  ArrowRight,
  ExternalLink,
  Home,

  // UI Actions
  Layout,
  MessageSquare,
  Bell,
  PanelLeft,
  PanelLeftClose,
  Settings,
  RefreshCw,

  // State
  Variable,
  Code,

  // Flow Control actions
  Route,
  Timer,
  StopCircle,

  // Auth
  LogIn,
  LogOut,
  UserPlus,
  Shield,

  // Storage
  HardDrive,
  Save,
  Trash,

  // Crypto
  Hash,
  Lock,
  Unlock,

  // Form
  CheckSquare,
  Send,
  RotateCcw,

  // Debug
  Terminal,

  // Generic / utility
  Plus,
  Pencil,
  Trash2,
  X,
  MoreHorizontal,

  // Toolbar
  Undo2,
  Redo2,
  Download,
  Upload,
  PanelRight,
  PanelRightClose,
  Cloud,

  // Node UI (quick bar, menus)
  CopyPlus,
  LayoutGrid,
  ChevronDown,
  ChevronRight,

  // Inspector
  MousePointerClick,

  // Flow list
  PlayCircle,
  Clock,

  // Toast
  Check,
  AlertCircle,
  AlertTriangle,
  Info,

  // Chat
  Bot,
  User,

  // Story
  BookOpen,

  // Theme
  Sun,
  Moon,

  // Quality gate
  ShieldCheck,
  ShieldX,
  TrendingUp,
  TrendingDown,

  // Palette extras
  Star,
  Clipboard,
  FileJson,
  Printer,
  Camera,
  BarChart,
  CreditCard,
  Globe2,
  Smartphone,
  Tablet,
  Monitor,
  Wrench,
  ClipboardCheck,

  // Event hierarchy tree
  Box,
  FileText,

  // Block files
  FolderOpen,
} from 'lucide-react'

export type { LucideIcon }

// ---- Icon Map (Page Editor parity: string key → Lucide component) ----

export const ICON_MAP: Record<string, LucideIcon> = {
  // Node types
  Zap,
  Play,
  PhoneIncoming,
  GitBranch,
  Repeat,
  Layers,
  Group,
  StickyNote,
  GitFork,

  // API & Data
  Globe,
  Database,
  Search,
  Sparkles,

  // Navigation
  Compass,
  ArrowLeft,
  ArrowRight,
  ExternalLink,
  Home,

  // UI Actions
  Layout,
  MessageSquare,
  Bell,
  PanelLeft,
  PanelLeftClose,
  Settings,
  RefreshCw,

  // State
  Variable,
  Code,

  // Flow Control
  Route,
  Timer,
  StopCircle,

  // Auth
  LogIn,
  LogOut,
  UserPlus,
  Shield,

  // Storage
  HardDrive,
  Save,
  Trash,

  // Crypto
  Hash,
  Lock,
  Unlock,

  // Form
  CheckSquare,
  Send,
  RotateCcw,

  // Debug
  Terminal,

  // Utility
  Plus,
  Pencil,
  Trash2,
  X,
  MoreHorizontal,

  // Toolbar
  Undo2,
  Redo2,
  Download,
  Upload,
  PanelRight,
  PanelRightClose,
  Cloud,

  // Node UI
  CopyPlus,
  LayoutGrid,
  ChevronDown,
  ChevronRight,

  // Inspector
  MousePointerClick,

  // Flow list
  PlayCircle,
  Clock,

  // Toast
  Check,
  AlertCircle,
  AlertTriangle,
  Info,

  // Chat
  Bot,
  User,
  BookOpen,

  // Theme
  Sun,
  Moon,

  // Quality gate
  ShieldCheck,
  ShieldX,
  TrendingUp,
  TrendingDown,

  // Palette extras
  Star,
  Clipboard,
  FileJson,
  Printer,
  Camera,
  BarChart,
  CreditCard,
  Globe2,
  Smartphone,
  Tablet,
  Monitor,
  Wrench,

  // Spec contract
  ClipboardCheck,

  // Event hierarchy tree
  Box,
  FileText,

  // Block files
  FolderOpen,
}

/** Resolve icon name → Lucide component (Page Editor ICON_MAP pattern). */
export function getIcon(name: string): LucideIcon {
  return ICON_MAP[name] ?? Play
}
